<template>
  <v-card class="main-card">
    <v-card-title class="screen-title">
      <span>アプライアンスログ一覧</span>
      <manual-button path="/report-appliance/" />
    </v-card-title>
    <v-card-text v-if="isLoaded" class="pa-5">
      <div class="mb-6">
        お客様使用量
        {{
          applianceLogInformation
            ? `${applianceLogInformation.capacityUsed}GB`
            : "-"
        }}
        / お客様ご利用可能ログ容量
        {{
          applianceLogInformation
            ? `${applianceLogInformation.applianceLogCapacity}GB`
            : "-"
        }}
      </div>

      <v-card class="mb-6" v-permission.config>
        <v-card-text>
          <h3 class="mb-2">当日アプライアンスログ作成</h3>

          <validation-observer v-slot="{ handleSubmit }" as="div">
            <v-form
              novalidate
              @submit.stop.prevent="handleSubmit(submit)"
              class="pa-5"
            >
              <v-row>
                <v-col>
                  <app-label-group
                    label="作成対象日(当日)"
                    :value="targetDate"
                    class="mb-2"
                  />
                </v-col>
              </v-row>

              <app-badge
                item="アプライアンス"
                :isRequired="true"
                :isInfo="false"
              />
              <v-row>
                <v-col>
                  <validation-provider
                    name="appliance"
                    label="アプライアンス"
                    v-model="form.applianceSelected"
                    :rules="{ required: true }"
                    v-slot="{ componentField, errorMessage }"
                  >
                    <app-multi-select
                      v-bind="componentField"
                      :error-messages="errorMessage"
                      :options="applianceOptions"
                      :custom-label="applianceCustomLabel"
                      :multiple="false"
                      label="lineNum"
                      track-by="lineNum"
                      group-label="applianceLabel"
                      group-values="applianceValues"
                    >
                      <template #singleLabel="{ option }">
                        <template v-if="form.applianceSelected">
                          <div class="multiselect-tbody">
                            <p class="v-col-3 pa-0">
                              {{ $filter.emptyTo(option.lineNum, "-") }}
                            </p>
                            <p class="v-col-3 pa-0">
                              {{ $filter.emptyTo(option.type4Id, "-") }}
                            </p>
                            <p class="v-col-5 pa-0">
                              {{
                                $filter.enumTo(
                                  option.applianceType,
                                  "applianceType"
                                )
                              }}
                            </p>
                          </div>
                        </template>
                      </template>

                      <template #option="{ option }">
                        <template v-if="option.$isLabel">
                          <div class="multiselect-thead">
                            <p class="v-col-3 pa-0">
                              {{ option.$groupLabel.lineNum }}
                            </p>
                            <p class="v-col-3 pa-0">
                              {{ option.$groupLabel.type4Id }}
                            </p>
                            <p class="v-col-5 pa-0">
                              {{ option.$groupLabel.applianceType }}
                            </p>
                          </div>
                        </template>
                        <template v-else>
                          <div class="multiselect-tbody">
                            <p class="v-col-3 pa-0">
                              {{ $filter.emptyTo(option.lineNum, "-") }}
                            </p>
                            <p class="v-col-3 pa-0">
                              {{ $filter.emptyTo(option.type4Id, "-") }}
                            </p>
                            <p class="v-col-5 pa-0">
                              {{
                                $filter.enumTo(
                                  option.applianceType,
                                  "applianceType"
                                )
                              }}
                            </p>
                          </div>
                        </template>
                      </template>
                    </app-multi-select>
                  </validation-provider>
                </v-col>
              </v-row>

              <div class="mt-3">期間</div>

              <v-row class="ml-3 mr-3">
                <v-col cols="3" class="pr-13">
                  <app-badge
                    item="開始時刻"
                    :isRequired="true"
                    :isInfo="false"
                  />
                  <validation-provider
                    name="timeFrom"
                    label="開始時刻"
                    v-model="form.timeFrom"
                    :rules="{ required: true, is_time: true }"
                    v-slot="{ componentField, errorMessage, meta }"
                  >
                    <app-form-input
                      type="time"
                      v-bind="componentField"
                      :error-messages="errorMessage"
                      :field-meta="meta"
                      class="time-content"
                    />
                  </validation-provider>
                </v-col>
                <v-col class="pl-13">
                  <app-badge
                    item="終了時刻"
                    :isRequired="true"
                    :isInfo="false"
                  />
                  <validation-provider
                    name="timeTo"
                    label="終了時刻"
                    v-model="form.timeTo"
                    :rules="{
                      required: true,
                      is_time: true,
                      is_datetime_range: {
                        from: form.timeFrom,
                        format: 'HH:mm',
                      },
                    }"
                    v-slot="{ componentField, errorMessage, meta }"
                  >
                    <app-form-input
                      type="time"
                      v-bind="componentField"
                      :error-messages="errorMessage"
                      :field-meta="meta"
                      class="time-content"
                    />
                  </validation-provider>
                </v-col>
              </v-row>

              <div class="d-flex justify-end">
                <v-btn type="submit" color="primary">ファイル作成</v-btn>
              </div>
            </v-form>
          </validation-observer>
        </v-card-text>
      </v-card>

      <v-row no-gutters>
        <v-col>
          <app-search
            :search="applianceLog.search"
            v-model="applianceLog.filter"
            :initial-form="
              () => ({
                reportDate: { start: null, end: null },
              })
            "
          >
            <!-- レポート対象日の期間検索 -->
            <template #input(custom)="{ form }">
              <v-row class="d-flex date-time">
                <v-col>
                  <validation-provider
                    name="start"
                    label="開始"
                    v-model="form['reportDate']['start']"
                    :rules="{ required: true, date_format: 'YYYY-MM-DD' }"
                    v-slot="{ componentField, errorMessage, meta }"
                  >
                    <app-form-input
                      v-bind="componentField"
                      :error-messages="errorMessage"
                      :field-meta="meta"
                      type="date"
                      hide-details
                    ></app-form-input>
                  </validation-provider>
                </v-col>
                <span class="ml-2 mr-2">〜</span>
                <v-col>
                  <validation-provider
                    name="end"
                    label="終了"
                    v-model="form['reportDate']['end']"
                    :rules="{ required: true, date_format: 'YYYY-MM-DD' }"
                    v-slot="{ componentField, errorMessage, meta }"
                  >
                    <app-form-input
                      v-bind="componentField"
                      :error-messages="errorMessage"
                      :field-meta="meta"
                      type="date"
                      hide-details
                    ></app-form-input>
                  </validation-provider>
                </v-col>
                <v-col>
                  <v-btn icon="$mdiMagnify" type="submit"></v-btn>
                </v-col>
              </v-row>
            </template>
            <!-- レポート対象日のタグ表示 -->
            <template #tagValue(reportDate)="{ value }">
              {{ $filter.date(value.start) }} ~ {{ $filter.date(value.end) }}
            </template>
          </app-search>
        </v-col>
        <div class="d-flex flex-row ga-1 flex-wrap ml-2">
          <v-btn v-permission.config color="primary" @click="showDel"
            >削除</v-btn
          >
        </div>
      </v-row>

      <v-alert class="mt-2 mb-4" v-model="showAlert">{{
        $filter.message("no_select_data")
      }}</v-alert>
      <div>
        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="reload">一覧更新</v-btn>
        </div>
        <app-table
          primary-key="fileName"
          :fields="applianceLog.fields"
          :items="applianceLog.items"
          :selectable="selectable"
          v-model="applianceLog.selected"
          :filter="applianceLog.filter"
          :filter-option="applianceLog.filter_option"
          v-model:table-sort-by="applianceLog.sortBy"
          sticky-header="50vh"
        >
          <!-- レポート対象日 -->
          <template #cell(reportDate)="data">
            <div>{{ $filter.date(data.value) }}</div>
          </template>
          <!-- アプライアンス名 -->
          <template #cell(applianceType)="data">
            <div>{{ $filter.enumTo(data.value, "applianceType") }}</div>
          </template>
          <!-- ファイル名 -->
          <template #cell(fileName)="data">
            <a href="#" @click="showDL(data.value)">{{ data.value }}</a>
          </template>
          <!-- ファイルサイズ -->
          <template #cell(fileSize)="data">
            <div class="text-right">
              {{ convertFileSize(data.item.fileSize) }}
            </div>
          </template>
        </app-table>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { FilterOption } from "@/components/AppTable.vue";
import type { SearchOption } from "@/components/AppSearch.vue";
import type { ApplianceLogInformation } from "@/apis/ApplianceLogInformationApi";
import type {
  Appliance,
  ApplianceLog,
  CreateApplianceLog,
} from "@/apis/ApplianceLogApi";
import { ApiInternalServerError, ApiNotFoundError } from "@/error/errors";
import ApiErrorModal from "@/modals/error/ApiErrorModal.vue";
import ApiNotFoundModal from "@/modals/error/ApiNotFoundModal.vue";
import type { ContractAppliances } from "@/apis/ContractApi";
import type { ApplianceLogList } from "@/apis/ApplianceLogApi";
import type { AppTableHeader, SortItem } from "@/components/AppTable.vue";

export default defineComponent({
  name: "ApplianceLogList",
  data() {
    return {
      applianceLogInformation: {} as ApplianceLogInformation | null,
      targetDate: this.$filter.date(new Date()),
      applianceOptions: [
        {
          applianceLabel: {
            lineNum: "回線番号",
            type4Id: "Type4ID",
            applianceType: "アプライアンス名",
          },
          applianceValues: [] as Appliance[],
        },
      ],
      form: {
        applianceSelected: null as Appliance | null,
        timeFrom: "",
        timeTo: "",
      },
      applianceLog: {
        sortBy: { key: "reportDate", order: "desc" } as SortItem,
        fields: [
          {
            key: "reportDate",
            title: "レポート対象日",
            width: "150px",
          },
          {
            key: "lineNum",
            title: "回線番号",
            width: "120px",
            filterByFormatted: true,
            formatter: (value: string, key: null, item: ApplianceLog) => {
              if (item.applianceType === "INTRANET_FW") {
                return this.$store.state.user.contractInfo.vnumber;
              } else {
                if (item.enumber) {
                  return item.enumber;
                } else {
                  return item.wnumber;
                }
              }
            },
          },
          {
            key: "applianceType",
            title: "アプライアンス名",
            width: "170px",
          },
          {
            key: "fileName",
            title: "ファイル名",
            width: "200px",
          },
          {
            key: "fileSize",
            title: "ファイルサイズ",
            width: "140px",
          },
        ] as AppTableHeader<ApplianceLog>[],
        items: [] as ApplianceLog[],
        selected: [] as ApplianceLog[],
        search: {
          reportDate: { type: "custom", label: "レポート対象日" },
          lineNum: { type: "text", label: "回線番号" },
          applianceType: {
            type: "select",
            label: "アプライアンス名",
            items: [
              { value: "UTM", text: "UTM" },
              { value: "INTERNET_FW", text: "インターネットFW" },
              { value: "INTRANET_FW", text: "イントラネットFW" },
            ],
          },
        } as SearchOption,

        filter: {} as Record<string, unknown>,
        filter_option: {
          reportDate: (
            item: ApplianceLog,
            value: { start: string; end: string }
          ) => {
            return this.$moment(item.reportDate).isBetween(
              this.$moment(value.start),
              this.$moment(value.end),
              undefined,
              "[]"
            );
          },
          lineNum: "like",
          applianceType: "equal",
        } as FilterOption,
      },
      showAlert: false,
      // 初期通信完了フラグ
      isLoaded: false,
    };
  },
  computed: {
    selectable(): boolean {
      return this.$service.permission.hasAuthority("CONFIG");
    },
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    /** アプライアンス選択肢ラベル名 */
    applianceCustomLabel({
      lineNum,
      type4Id,
      applianceType,
    }: Appliance): string {
      return `${lineNum} ${type4Id} ${this.$filter.enumTo(
        applianceType,
        "applianceType"
      )} `;
    },
    convertFileSize(size: number): string {
      let target = size;
      let unit = "KB";

      if (size >= gb) {
        target = gb;
        unit = "GB";
      } else if (size >= mb) {
        target = mb;
        unit = "MB";
      }
      return `${
        size >= mb ? Math.round((size / target) * 10) / 10 : size
      }${unit}`;
    },
    /** 一覧を取得 */
    async load() {
      try {
        this.applianceLogInformation =
          await this.$api.applianceLogInformationApi.getApplianceLogInformation();
      } catch (e) {
        // 404 契約無しの場合に発生する場合
        if (e instanceof ApiNotFoundError) {
          this.applianceLogInformation = null;
        } else {
          // 500 ディスク使用量の取得に失敗
          if (
            e instanceof ApiInternalServerError &&
            e.data.errorCode === "CC06013E"
          ) {
            await this.$modal.show(ApiErrorModal, { error: e });
            return;
          }
          throw e;
        }
      }

      const appliances = await this.$api.contract.getAppliances();
      const applianceList = (
        await this.$api.applianceLogApi.getApplianceLogCreatableList()
      ).applianceList.map(
        (e: {
          enumber: string | null;
          wnumber: string | null;
          applianceType: "UTM" | "INTERNET_FW" | "INTRANET_FW";
        }) => ({
          ...e,
          type4Id:
            appliances.applianceInfoList.find(
              (v: ContractAppliances["applianceInfoList"][0]) =>
                v.wnumberMainAct === e.wnumber
            )?.type4Id ?? null,
          lineNum:
            e.applianceType === "INTRANET_FW"
              ? this.$store.state.user.contractInfo.vnumber
              : e.enumber || e.wnumber,
        })
      );
      if (applianceList.length !== 0) {
        this.applianceOptions[0].applianceValues = applianceList;
      } else {
        this.applianceOptions = [];
      }

      try {
        this.applianceLog.items = (
          await this.$api.applianceLogApi.getApplianceLog()
        ).applianceLogList;
      } catch (e) {
        // 404の場合、エラーを外に出さずに
        if (e instanceof ApiNotFoundError) {
          return;
        }
        // 404以外の場合、エラーを外に出す
        throw e;
      }
    },
    /** 当日ログファイル作成 */
    async submit() {
      this.showAlert = false;
      await this.$confirm(
        `指定された時間のファイルを作成します。よろしいですか？`
      );
      const postData: CreateApplianceLog = {
        enumber: this.form.applianceSelected?.enumber || null,
        wnumber: this.form.applianceSelected?.wnumber || null,
        applianceType: this.form.applianceSelected!.applianceType,
        startDateTime:
          this.$moment(this.targetDate + " " + this.form.timeFrom).format(
            "YYYY-MM-DDTHH:mm:00"
          ) + "+09:00",
        endDateTime:
          this.$moment(this.targetDate + " " + this.form.timeTo).format(
            "YYYY-MM-DDTHH:mm:00"
          ) + "+09:00",
      };
      await this.$api.applianceLogApi.postCreateApplianceLog(postData);
      await this.reload();
    },
    /** 一覧更新 */
    async reload() {
      this.showAlert = false;

      try {
        this.applianceLog.items = (
          await this.$api.applianceLogApi.getApplianceLog()
        ).applianceLogList;
      } catch (e) {
        // 404の場合、エラーを外に出さずに
        if (e instanceof ApiNotFoundError) {
          return;
        }
        // 404以外の場合、エラーを外に出す
        throw e;
      }
    },
    /** 削除 */
    async showDel() {
      if (this.applianceLog.selected.length === 0) {
        this.showAlert = true;
        return;
      }
      this.showAlert = false;
      await this.$confirm(`選択されたログを削除します。よろしいですか？`);
      const deleteFiles = this.applianceLog.selected.map((e) => e.fileName);
      try {
        await this.$api.applianceLogApi.deleteApplianceLog(deleteFiles);
        this.applianceLog.selected = [];
        await this.reload();
      } catch (e) {
        // 500 ダウンロードに失敗
        if (
          e instanceof ApiInternalServerError &&
          e.data.errorCode === "CC99012E"
        ) {
          await this.$modal.show(ApiErrorModal, { error: e });
          return;
        }
        throw e;
      }
    },
    /** ファイルダウンロード確認 */
    async showDL(fileName: string) {
      this.showAlert = false;
      /** ダウンロードする前にダウンロード対象のデータが存在するかの確認 */
      const fileCheckItem: boolean = (
        await this.$api.applianceLogApi.getApplianceLog()
      ).applianceLogList.some(
        (e: ApplianceLogList["applianceLogList"][0]) => e.fileName === fileName
      );

      if (fileCheckItem) {
        await this.$confirm(
          `指定されたファイルをダウンロードします。よろしいですか？`
        );
        window.open(this.$api.applianceLogApi.downloadFile(fileName), "_blank");
      } else {
        await this.$modal.show(ApiNotFoundModal);
      }
    },
  },
});

const mb = 1024;
const gb = 1024 * 1024;
</script>

<style lang="scss" scoped>
:deep(.v-card) {
  overflow: unset;
  position: unset;
}

.time-content {
  width: 140px;
}
.date-time {
  align-items: center;
}

:deep(.v-data-table__tr--clickable) {
  cursor: default;
}
</style>
